// @ts-strict-ignore
import { FireOutlined, UndoOutlined } from '@ant-design/icons';
import * as React from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import Tooltip from 'antd/lib/tooltip';
import Text from 'antd/lib/typography/Text';

import {
    getIsGooglePlayStore,
    getIsHuaweiAppGallery,
    isAppPlatform,
    isPhasedReleaseActive,
    isPhasedReleaseComplete,
    isPhasedReleasePaused,
    isReleaseSkipped,
    statusColorForReleaseStatus,
} from '@/helpers';
import {
    convertAndroidStagedRolloutPercentage,
    convertUserFraction,
    displayStringForRolloutPercentage,
} from '@/helpers/displayHelpers';
import { RootState } from '@/reducers/reducers';
import { selectAppPlatform, selectAppStoreType, selectRelease } from '@/selectors/selectors';
import { ReleaseStatus, ReleaseTimelinePhase, ReleaseType } from '@/types';
import { antGrey, runwayColors } from '../styles/globalStyles';

interface DestinationBreakdownItem {
    name: string;
    percent: number;
    isComplete: boolean;
}

export interface ReleaseNodeDisplay {
    statusColor: string;
    dotColor: string;
    showPhasedHalfFilled: boolean;
    isPhasedRolloutInProgress: boolean;
    isRollback: boolean;
    icon: React.ReactNode;
    isSkipped: boolean;
    displayedRolloutPercentage: number;
    destinationBreakdown: DestinationBreakdownItem[];
    isMultiDestination: boolean;
    allDestinationsAligned: boolean;
}

export const useReleaseNodeDisplay = (releaseId: string, appId: string): ReleaseNodeDisplay => {
    const {
        releaseStatus,
        releaseTimelinePhase,
        releaseType,
        releaseSourceVersion,
        phasedReleaseHasBeenSupersededByNewRelease,
    } = useSelector((state: RootState) => {
        const release = selectRelease(state, releaseId);
        return {
            releaseStatus: release.status,
            releaseTimelinePhase: release.timelinePhase,
            releaseType: release.type,
            releaseSourceVersion: release.sourceVersion,
            phasedReleaseHasBeenSupersededByNewRelease: release.phasedReleaseHasBeenSupersededByNewRelease,
        };
    }, shallowEqual);
    const phasedRelease = useSelector(
        (state: RootState) => selectRelease(state, releaseId).phasedRelease,
        shallowEqual,
    );
    const destinationPhasedReleases = useSelector(
        (state: RootState) => selectRelease(state, releaseId).destinationPhasedReleases || [],
        shallowEqual,
    );
    const rollbackTargetVersionInfo = useSelector(
        (state: RootState) => selectRelease(state, releaseId).rollbackTargetVersionInfo,
        shallowEqual,
    );
    const appStoreType = useSelector((state: RootState) => selectAppStoreType(state, appId));
    const platform = useSelector((state: RootState) => selectAppPlatform(state, appId));

    const statusColor = statusColorForReleaseStatus(releaseStatus);
    const releaseCompleted = releaseStatus === ReleaseStatus.Completed;

    const getPhasedReleaseData = (): {
        isPhased: boolean;
        phasedReleaseComplete: boolean;
        phasedReleasePercent: number;
        isHalted: boolean;
        isManuallyAcceleratedPhasedRollout: boolean;
        phasedRolloutHasStarted: boolean;
        destinationBreakdown: DestinationBreakdownItem[];
    } => {
        if (destinationPhasedReleases.length) {
            let completedPhasedReleasesCount = 0;
            let phasedReleasePercent = null;
            let isHalted = false;
            let isManuallyAcceleratedPhasedRollout = false;
            const destinationBreakdown: DestinationBreakdownItem[] = [];

            for (const destinationPhasedRelease of destinationPhasedReleases) {
                const isComplete = isPhasedReleaseComplete(destinationPhasedRelease);
                destinationBreakdown.push({
                    name: destinationPhasedRelease.destination?.name ?? '',
                    percent: isComplete ? 100 : destinationPhasedRelease.phasedReleasePercent,
                    isComplete,
                });

                if (isComplete) {
                    completedPhasedReleasesCount++;
                    continue;
                }

                if (
                    destinationPhasedRelease.phasedReleasePercent !== null &&
                    destinationPhasedRelease.phasedReleasePercent !== 100 &&
                    (phasedReleasePercent === null ||
                        destinationPhasedRelease.phasedReleasePercent < phasedReleasePercent)
                ) {
                    phasedReleasePercent = destinationPhasedRelease.phasedReleasePercent;
                    isManuallyAcceleratedPhasedRollout = destinationPhasedRelease.isManuallyAcceleratedPhasedRollout;
                    isHalted = isPhasedReleasePaused(destinationPhasedRelease.state);
                }
            }

            const phasedReleaseComplete = completedPhasedReleasesCount === destinationPhasedReleases.length;
            const phasedRolloutHasStarted = destinationBreakdown.some((dest) => !dest.isComplete && dest.percent > 0);
            return {
                isPhased: true,
                phasedReleaseComplete,
                phasedReleasePercent,
                isHalted,
                isManuallyAcceleratedPhasedRollout,
                phasedRolloutHasStarted,
                destinationBreakdown,
            };
        }

        const isPhased = !!phasedRelease;
        const phasedReleaseComplete = isPhasedReleaseComplete(phasedRelease);
        const isHalted = isPhasedReleasePaused(phasedRelease?.state);

        return {
            isPhased,
            isHalted,
            phasedReleaseComplete,
            phasedReleasePercent: phasedRelease?.phasedReleasePercent,
            isManuallyAcceleratedPhasedRollout: phasedRelease?.isManuallyAcceleratedPhasedRollout,
            phasedRolloutHasStarted: isPhasedReleaseActive(phasedRelease?.state),
            destinationBreakdown: [],
        };
    };

    const {
        isPhased,
        isHalted,
        isManuallyAcceleratedPhasedRollout,
        phasedReleaseComplete,
        phasedReleasePercent,
        phasedRolloutHasStarted,
        destinationBreakdown,
    } = getPhasedReleaseData();

    let phasedReleaseActiveOrSuperseded =
        isPhased &&
        releaseCompleted &&
        phasedRolloutHasStarted &&
        phasedReleasePercent !== null &&
        phasedReleasePercent !== 100;

    if (phasedReleaseHasBeenSupersededByNewRelease) {
        phasedReleaseActiveOrSuperseded = true;
    }

    const showPhasedHalfFilled = (phasedReleaseActiveOrSuperseded || isHalted) && !isManuallyAcceleratedPhasedRollout;
    const colorOverride = showPhasedHalfFilled && isHalted ? runwayColors.statusRed : null;

    const hasDestinationPhasedData = destinationPhasedReleases.length > 0;
    const isPhasedRolloutInProgress = isPhased && releaseCompleted && !phasedReleaseComplete && phasedRolloutHasStarted;

    const isGooglePlayStore = getIsGooglePlayStore(appStoreType);
    const isHuaweiAppGallery = getIsHuaweiAppGallery(appStoreType);
    const rolloutComplete = isPhasedReleaseComplete(phasedRelease);

    const userFraction =
        isGooglePlayStore || isHuaweiAppGallery ? phasedRelease?.userFraction
        : isAppPlatform(platform) ?
            phasedRelease?.isManuallyAcceleratedPhasedRollout || phasedRelease?.isRunwayAcceleratedRollout ?
                1
            :   phasedRelease?.userFraction
        :   null;

    const rolloutPercentage =
        isGooglePlayStore ?
            (
                rolloutComplete // when android release is complete show 100 because userFraction returns 0
            ) ?
                100
            :   convertAndroidStagedRolloutPercentage(userFraction)
        :   convertUserFraction(userFraction);

    const averageDestinationPercent =
        destinationBreakdown.reduce((sum, dest) => sum + dest.percent, 0) / destinationBreakdown.length;
    const displayedRolloutPercentage =
        Math.round((hasDestinationPhasedData ? averageDestinationPercent : rolloutPercentage) * 10) / 10;

    const isMultiDestination = destinationBreakdown.length > 1;
    const allDestinationsAligned =
        isMultiDestination && destinationBreakdown.every((dest) => dest.percent === destinationBreakdown[0].percent);

    const isRollback = releaseType === ReleaseType.Rollback && releaseTimelinePhase === ReleaseTimelinePhase.Rollback;
    const icon = rollbackOrHotfixIcon(releaseType, rollbackTargetVersionInfo?.version, releaseSourceVersion);
    return {
        statusColor,
        dotColor: colorOverride ?? statusColor,
        showPhasedHalfFilled,
        isPhasedRolloutInProgress,
        isRollback,
        icon,
        isSkipped: isReleaseSkipped(releaseStatus),
        displayedRolloutPercentage,
        destinationBreakdown,
        isMultiDestination,
        allDestinationsAligned,
    };
};

/** Rollback/hotfix indicator icon shown next to a release version. */
export const rollbackOrHotfixIcon = (
    releaseType: ReleaseType,
    targetVersion?: string,
    sourceVersion?: string,
): React.ReactNode => {
    switch (releaseType) {
        case ReleaseType.Hotfix:
            return (
                <Tooltip title="This release was a hotfix">
                    <FireOutlined style={{ color: runwayColors.statusYellow }} />
                </Tooltip>
            );
        case ReleaseType.Rollback:
            return (
                <Tooltip
                    title={`Rollback of ${targetVersion ? `${targetVersion}` : `the previous release`}${
                        sourceVersion ? ` to ${sourceVersion}.` : ''
                    }
                    `}
                >
                    <UndoOutlined style={{ color: antGrey.grey7 }} />
                </Tooltip>
            );
        default:
            return null;
    }
};

/** Diagonal-stripe background applied to rollback release rows. */
export const rollbackStripeBackground = (stripeA: string, stripeB: string): React.CSSProperties => ({
    background: `repeating-linear-gradient(
                -45deg,
                ${stripeA} 0px,
                ${stripeA} 3px,
                ${stripeB} 3px,
                ${stripeB} 6px)`,
});

interface PhasedRolloutBadgeProps {
    display: Pick<
        ReleaseNodeDisplay,
        'displayedRolloutPercentage' | 'destinationBreakdown' | 'isMultiDestination' | 'allDestinationsAligned'
    >;
    /** Text color — pass the row's version text color so the badge matches. */
    color: string;
    textStyle: React.CSSProperties;
}

/** "Rolling out X%" label, with a per-destination breakdown tooltip when the release spans destinations. */
export const PhasedRolloutBadge: React.FC<PhasedRolloutBadgeProps> = ({ display, color, textStyle }) => {
    const { displayedRolloutPercentage, destinationBreakdown, isMultiDestination, allDestinationsAligned } = display;

    const text = (
        <Text style={{ ...textStyle, color }}>
            Rolling out {displayStringForRolloutPercentage(displayedRolloutPercentage)}
        </Text>
    );

    if (!isMultiDestination) {
        return text;
    }

    const tooltip =
        allDestinationsAligned ?
            <div>
                <div>{`${destinationBreakdown.length} destinations at ${displayStringForRolloutPercentage(displayedRolloutPercentage)}:`}</div>
                <ul style={{ paddingLeft: 16, marginBottom: 0, marginTop: 4, maxHeight: '12rem', overflowY: 'auto' }}>
                    {destinationBreakdown.map((dest, i) => (
                        <li key={dest.name || i}>{dest.name}</li>
                    ))}
                </ul>
            </div>
        :   <div>
                <div>{`${destinationBreakdown.length} destinations:`}</div>
                <ul style={{ paddingLeft: 16, marginBottom: 0, marginTop: 4, maxHeight: '12rem', overflowY: 'auto' }}>
                    {destinationBreakdown.map((dest, i) => (
                        <li key={dest.name || i}>
                            {dest.name}: {displayStringForRolloutPercentage(dest.percent)}
                        </li>
                    ))}
                </ul>
            </div>;

    return (
        <Tooltip title={tooltip} placement="right">
            {text}
        </Tooltip>
    );
};
